/** Shared ``identity/session`` command client for bootstrap + websocket token. */

import { detectBrowserWorkspaceTimeZone } from "@/lib/workspace-timezones"
import { CommandPage } from "@/lib/command"
import {
  parseClientBootstrapFromSessionData,
  parseIdentitySetupFromPayload,
  parseWebsocketCredentials,
  type IdentitySetup,
  type SessionClientBootstrap,
  type WebsocketCredentials,
} from "@/lib/identity-setup"

export const IDENTITY_SESSION_COMMAND_PATH = "/app/crm/base/cmd/identity/session"

const sessionPage = CommandPage(IDENTITY_SESSION_COMMAND_PATH)

export type IdentitySessionResult = {
  setup: IdentitySetup
  bootstrap: SessionClientBootstrap
}

export type IdentitySessionCompleteData = {
  complete: true
  workspace_setup_data?: {
    time_zone?: string
  }
}

/** Load setup flags and client bootstrap (includes ``websocket`` when configured). */
export async function requestIdentitySession(
  complete: boolean | IdentitySessionCompleteData = false,
): Promise<IdentitySessionResult> {
  const payload =
    complete === true
      ? {
          complete: true,
          workspace_setup_data: {
            time_zone: detectBrowserWorkspaceTimeZone(),
          },
        }
      : complete === false
        ? {}
        : complete
  const res = await sessionPage.session(payload)
  if (!res.ok || !res.data) {
    throw new Error(res.error ?? "Session command failed")
  }
  const setup = parseIdentitySetupFromPayload({ data: res.data })
  if (!setup) {
    throw new Error("Missing setup in response")
  }
  const bootstrap = parseClientBootstrapFromSessionData(res.data)
  if (!bootstrap) {
    throw new Error("Missing workspace/user bootstrap in session response")
  }
  return { setup, bootstrap }
}

/** Re-fetch websocket credentials via ``identity/session`` (token refresh / reconnect). */
export async function fetchWebsocketCredentialsFromSession(): Promise<WebsocketCredentials | null> {
  const { bootstrap } = await requestIdentitySession(false)
  return parseWebsocketCredentials(bootstrap.websocket) ?? null
}
